/* eslint-disable max-len */
import FinishedDelivery, { PutDownLocation } from '../../models/entities/FinishedDelivery';
import DeliveryModes from '../../models/enumerations/DeliveryMode';
import {
  UndeliverableReasonShortLabels,
  UndeliverableReasonBadgeAbbreviation,
} from '../../../metrics2/models/enumerations/UndeliverableReason';
import CustomerTrackingData from '../../models/types/CustomerTrackingData';
import ReturnTypes from '../../models/enumerations/ReturnType';
import { recipientPersonMapping, DeliveryStatusToUndeliveredReasonMap } from './GetDeliveryBadgeMappings';
import { countBy, uniq } from 'lodash';
import { badgeDictionary } from '@components/delivery-badge/types';
import { DeliveryStatus } from '../../constants/DeliveryStatus';
import { DeliveryItemTrackingData } from '../../models/types/DeliveryItemTrackingData';

type BadgeType = 'normal' | 'mixed' | 'grouped';

export type DeliveryBadge = {
  value: string;
  description: string;
  type: BadgeType;
  packagesAmount?: number;
  stopsAmount?: number;
  color?: string;
  index?: number;
  grouping?: Record<string, number>;
};

export interface Rule {
  shouldApply: (finishedDelivery: FinishedDelivery, customer?: CustomerTrackingData) => boolean;
  getValue: (finishedDelivery: FinishedDelivery, customer?: CustomerTrackingData) => DeliveryBadge;
  getFilterValue?: (finishedDelivery: FinishedDelivery) => DeliveryBadge[];
}

const fallbackRule: Rule = {
  shouldApply: () => true,
  getValue: (finishedDelivery: FinishedDelivery) => ({
    value: 'UNB',
    description: finishedDelivery.deliveryMode,
    color: null,
    type: 'normal',
  }),
};

/**
 * This parcel's OWN outcome, falling back to the customer it belongs to.
 *
 * lma-processors b347+ writes `recipientPerson` per parcel (DMT-552) precisely because one stop can
 * mix them — one parcel handed to a neighbour, the rest to the recipient. Blobs written before b347
 * carry the value only on the customer, so the fallback keeps those rendering exactly as they do now.
 */
const recipientPersonOfParcel = (item: DeliveryItemTrackingData, finishedDelivery: FinishedDelivery): string | null =>
  item?.recipientPerson ?? finishedDelivery?.customerDeliveries?.[item?.customerRef]?.recipientPerson ?? null;

/**
 * The recipient-person values that decide the badge — per parcel, in a stable order.
 *
 * Reads the PER-PARCEL value first. The stop-level copy is a single value for the whole customer, so
 * on a mixed stop it can only ever describe one of the parcels. Measured on 2026-08-24 over 15,308
 * live parcels: 416 carry a value where their customer carries none, and 112 disagree with it
 * outright — 3.4% of parcels whose badge was decided by another parcel's outcome.
 *
 * This also matters for the address split: grouping a stop by address produces MORE customers, and
 * 110 of 1,289 multi-parcel stops carry mixed per-parcel values. Without this, splitting a stop would
 * hand the badge to whichever value sorts first alphabetically.
 */
const getRecipientPersons = (customer: CustomerTrackingData, finishedDelivery: FinishedDelivery): string[] => {
  const items = finishedDelivery?.deliveryItems ?? [];
  const scoped = customer ? items.filter((item) => String(item?.customerRef) === String(customer.customerRef)) : items;
  const perParcel = scoped
    .map((item) => recipientPersonOfParcel(item, finishedDelivery))
    .filter((person): person is string => !!person);
  if (perParcel.length > 0) {
    return perParcel.sort();
  }
  // No parcel resolves (pre-b347 blob, or a stop with no delivered parcels) — previous behaviour.
  if (customer && finishedDelivery.customerDeliveries?.[customer.customerRef]) {
    return [finishedDelivery.customerDeliveries[customer.customerRef].recipientPerson];
  }
  if (Object.keys(finishedDelivery.customerDeliveries ?? {}).length > 0) {
    return Object.values(finishedDelivery.customerDeliveries)
      .map((d) => d.recipientPerson)
      .sort();
  }
  return [];
};

const noFinishedDelivery: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => finishedDelivery == null,
  getValue: () => badgeDictionary.default,
};

const personDeliveryReturn: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery, customer?: CustomerTrackingData) => {
    const recipientPerson = getRecipientPersons(customer, finishedDelivery)[0];
    return (
      finishedDelivery.deliveryMode === DeliveryModes.person &&
      finishedDelivery.returnDeliveryItems.length > 0 &&
      !recipientPerson
    );
  },
  getValue: (finishedDelivery: FinishedDelivery) => {
    for (const deliveryItem of finishedDelivery.returnDeliveryItems) {
      switch (deliveryItem.returnParcelDetails.returnType) {
        case ReturnTypes.DBCourier:
          return badgeDictionary.DB;
        case ReturnTypes.NewParcel:
          if (deliveryItem.returnParcelDetails.clientType === 'ProPS') {
            return badgeDictionary.PRO;
          } else {
            return badgeDictionary.PRI;
          }
        case ReturnTypes.ReturnToSender:
          return badgeDictionary.RET;
        case ReturnTypes.Undeliverable:
          return badgeDictionary.RL;
      }
    }
    return badgeDictionary.SEZ;
  },
};

export const personDeliveryRule: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => {
    if (finishedDelivery.deliveryMode === DeliveryModes.person) return true;
  },
  getValue: (finishedDelivery: FinishedDelivery, customer?: CustomerTrackingData) => {
    const recipientPerson = getRecipientPersons(customer, finishedDelivery)[0];

    const mappedValue = recipientPersonMapping[recipientPerson];
    if (mappedValue) {
      return mappedValue;
    }
    return badgeDictionary.SEZ;
  },
};

const personDeliveryPlus: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery, customer?: CustomerTrackingData) => {
    const recipientPerson = getRecipientPersons(customer, finishedDelivery)[0];
    if (finishedDelivery.deliveryMode !== DeliveryModes.person) return false;
    if (customer || Object.keys(finishedDelivery.customerDeliveries).length === 0) {
      return false;
    }
    if (!recipientPersonMapping[recipientPerson]) {
      return false;
    }
    const mappedValue = recipientPersonMapping[recipientPerson];
    // "Is this stop mixed?" has to be asked of the PARCELS. Asking customerDeliveries only saw a mix
    // when the stop already had several customers, so a stop whose parcels went to the recipient AND
    // next door — one customer, two outcomes — never reached the OE+ badge and fell through to
    // personDeliveryRule, which renders whichever value sorts first. Measured 2026-08-24: 110 of
    // 1,289 multi-parcel stops carry mixed per-parcel values.
    const distinctModes = new Set(getRecipientPersons(null, finishedDelivery));
    return distinctModes.size > 1 && mappedValue.value.indexOf('+') < 0;
  },
  getValue: (finishedDelivery: FinishedDelivery) => {
    const recipientPersons = getRecipientPersons(null, finishedDelivery);
    const hasDifferentRecipientPersons = new Set(recipientPersons).size > 1;
    const grouping = hasDifferentRecipientPersons
      ? countBy(recipientPersons?.map((recipientPerson) => recipientPersonMapping[recipientPerson]?.value))
      : undefined;
    if (recipientPersons.includes('intendedReceiver') && hasDifferentRecipientPersons) {
      return {
        value: `${recipientPersonMapping[recipientPersons[0]]?.value}+`, // FIXME: first recipient person still used to define combined value
        description: 'Originalempfänger mit Nachbarschaftsabgabe',
        color: '#13DA1C',
        type: hasDifferentRecipientPersons ? 'grouped' : 'normal',
        grouping,
      };
    }

    return badgeDictionary.SEZ;
  },
  getFilterValue: (finishedDelivery: FinishedDelivery) => {
    const recipientPersons = getRecipientPersons(null, finishedDelivery);
    const hasDifferentRecipientPersons = new Set(recipientPersons).size > 1;
    const grouping = hasDifferentRecipientPersons
      ? countBy(recipientPersons?.map((recipientPerson) => recipientPersonMapping[recipientPerson]?.value))
      : undefined;
    return recipientPersons?.map((recipientPerson) => {
      const mappedValue = recipientPersonMapping[recipientPerson];
      if (mappedValue) {
        return {
          ...mappedValue,
          type: hasDifferentRecipientPersons ? 'grouped' : 'normal',
          grouping,
        };
      }
      return badgeDictionary.SEZ;
    });
  },
};

const undelivered: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => finishedDelivery.deliveryMode === DeliveryModes.undeliverable,
  getValue: (finishedDelivery: FinishedDelivery) => {
    const reasonLabel = UndeliverableReasonShortLabels[finishedDelivery.undeliverableReason];
    const reasonShortLabel = UndeliverableReasonBadgeAbbreviation[finishedDelivery.undeliverableReason];
    if (reasonShortLabel) {
      return {
        color: 'red',
        value: reasonShortLabel,
        description: reasonLabel,
        type: 'normal',
      };
    } else {
      return {
        ...badgeDictionary.RL,
        description: 'Rücklauf',
        color: 'red',
      };
    }
  },
};

const paketShop: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => finishedDelivery.deliveryMode === DeliveryModes.paketShop,
  getValue: () => {
    return badgeDictionary.PS;
  },
};

const props: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => finishedDelivery.deliveryMode === DeliveryModes.props,
  getValue: () => {
    return badgeDictionary.PRO;
  },
};

const onTimeAuth: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) =>
    finishedDelivery.deliveryMode === DeliveryModes.oneTimeAuthorizationPutDown,
  getValue: () => {
    return badgeDictionary.EV;
  },
};

export const putDownOutside: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => finishedDelivery.deliveryMode === DeliveryModes.putDownOutside,
  getValue: (finishedDelivery: FinishedDelivery) => {
    const mapping = putDownLocationMappingType[finishedDelivery.putDownLocation];
    if (mapping) {
      return mapping;
    }
    // the default is "DV" for backward compatibility reasons
    return badgeDictionary.DV;
  },
};

// The badge for ONE returned parcel, from that parcel's OWN closing scan status ("RL Z" -> Z).
//
// Three lookups in order, so this is TOTAL — it can never return undefined:
//   1. the reason map ("RL Z" -> TooLate -> Z), which is the mapping the stop-level rules use;
//   2. the status's own token ("RL T1" -> T1) — the TAN statuses are deliberately absent from
//      DeliveryStatusToUndeliveredReasonMap, and dropping to the generic RL badge for them would
//      lose the T1..T4 badges the previous code rendered correctly;
//   3. the neutral red RL badge, for the few ids that neither route resolves ("RL Storno",
//      "RL IG" — badgeDictionary has no IG key). That is exactly what the stop-level `undelivered`
//      rule already renders for them, so nothing regresses.
const badgeForReturnStatus = (status: string): DeliveryBadge => {
  const reason = DeliveryStatusToUndeliveredReasonMap[status as DeliveryStatus];
  const abbreviation = (reason ? UndeliverableReasonBadgeAbbreviation[reason] : undefined) ?? status?.split(' ')?.[1];
  const known = abbreviation ? badgeDictionary[abbreviation] : undefined;
  return known ?? { ...badgeDictionary.RL, description: 'Rücklauf', color: 'red' };
};

// The badge for ONE delivered parcel of a stop — the recipient-person mapping the stop-level
// `personDeliveryRule` uses, so a mixed stop renders its delivered parcels exactly as a fully
// delivered stop would.
const badgeForDeliveredParcel = (
  finishedDelivery: FinishedDelivery,
  deliveryItem?: DeliveryItemTrackingData
): DeliveryBadge => {
  // Prefer THIS parcel's outcome. Without it the stop's first value decided every delivered parcel of
  // a mixed stop, so a stop with one neighbour parcel badged them all NB (or none of them).
  //
  // A parcel that resolves to nothing — no per-parcel value and no customerRef to find one by — falls
  // back to the stop, which is exactly the previous behaviour. Only a parcel that actually knows its
  // own outcome overrides it, so this can add information but never take it away.
  const recipientPerson =
    (deliveryItem ? recipientPersonOfParcel(deliveryItem, finishedDelivery) : null) ??
    getRecipientPersons(null, finishedDelivery)[0];
  return recipientPersonMapping[recipientPerson] ?? badgeDictionary.SEZ;
};

// The badge for ONE pickup/return parcel — same switch personDeliveryReturn applies to the stop,
// evaluated per item so a mixed stop keeps its Abholung/Mitnahme parcels visible and counted.
const badgeForPickupParcel = (deliveryItem: DeliveryItemTrackingData): DeliveryBadge => {
  switch (deliveryItem?.returnParcelDetails?.returnType) {
    case ReturnTypes.DBCourier:
      return badgeDictionary.DB;
    case ReturnTypes.NewParcel:
      return deliveryItem.returnParcelDetails.clientType === 'ProPS' ? badgeDictionary.PRO : badgeDictionary.PRI;
    case ReturnTypes.ReturnToSender:
      return badgeDictionary.RET;
    case ReturnTypes.Undeliverable:
      return badgeDictionary.RL;
    default:
      return badgeDictionary.RET;
  }
};

// From 7th June on, deliveryItems have the status itself too, before only the parent "FinishedDelivery" had it
const isNewApi = (fd: FinishedDelivery) =>
  fd.deliveryItems.every((d) => d.status != null) && fd.returnDeliveryItems.every((d) => d.status != null);

const mixedReturnAndDelivery: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => {
    if (!isNewApi(finishedDelivery)) return false;
    const { deliveryItems } = finishedDelivery;

    return (
      deliveryItems.length > 0 &&
      deliveryItems.some((d) => d.status.startsWith('RL')) &&
      deliveryItems.some((d) => !d.status.startsWith('RL'))
    );
  },
  getValue: (finishedDelivery: FinishedDelivery) => {
    const all = [...finishedDelivery.returnDeliveryItems, ...finishedDelivery.deliveryItems].map((s) => s.status);
    const grouped = countBy(all);

    // The stop-list badge stays the dedicated "mixed" chip: FinishedDeliveryBadge renders that branch
    // with its own markup and the per-status tooltip, and ignores `value` entirely.
    return {
      value: '',
      description: '',
      type: 'mixed',
      grouping: grouped,
    };
  },
  // …but the Zustellart FILTER panel does read `value`, and without this the whole stop collapsed
  // into a single chip labelled '' — an unlabelled, unfilterable badge carrying every parcel of the
  // stop. Emit one badge per parcel instead, exactly like the sibling return rules: each returned
  // parcel by its own status, each delivered parcel by the stop's recipient-person badge, and each
  // Abholung/Mitnahme by its return type. Parcel totals are preserved (both arrays are covered), so
  // the panel counts still add up to the tour's parcel count.
  getFilterValue: (finishedDelivery: FinishedDelivery) => [
    ...finishedDelivery.deliveryItems.map((dItem) =>
      dItem?.status?.startsWith('RL')
        ? badgeForReturnStatus(dItem.status)
        : badgeForDeliveredParcel(finishedDelivery, dItem)
    ),
    ...finishedDelivery.returnDeliveryItems.map((dItem) => badgeForPickupParcel(dItem)),
  ],
};

const differentReturnStops: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => {
    if (!isNewApi(finishedDelivery)) return false;
    const { returnDeliveryItems } = finishedDelivery;
    if (!returnDeliveryItems.every((d) => !d.status.startsWith('RL N'))) {
      return false;
    }
    const uniqDeliveriesStatus = uniq(returnDeliveryItems.map((d) => d.status));
    return uniqDeliveriesStatus.length > 1;
  },
  getValue: (finishedDelivery: FinishedDelivery) => {
    const all = [...finishedDelivery.returnDeliveryItems, ...finishedDelivery.deliveryItems].map((s) => s.status);
    const grouped = countBy(all);

    return {
      ...badgeDictionary.RL,
      description: 'Rücklauf',
      color: 'red',
      type: 'grouped',
      grouping: grouped,
    };
  },
};

const differentReturnStopsWithN: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => {
    if (!isNewApi(finishedDelivery)) return false;
    const { deliveryItems } = finishedDelivery;
    return deliveryItems.some((d) => d.status.startsWith('RL N'));
  },
  getValue: (finishedDelivery: FinishedDelivery) => {
    const { deliveryItems } = finishedDelivery;
    const uniqDeliveriesStatus = uniq(deliveryItems.map((d) => d.status));
    if (uniqDeliveriesStatus.length === 1 && uniqDeliveriesStatus[0].startsWith('RL N')) {
      const orderOfN = uniqDeliveriesStatus[0].split(' ')[1];
      return badgeDictionary[orderOfN];
    }

    const all = [...finishedDelivery.returnDeliveryItems, ...finishedDelivery.deliveryItems].map((s) => s.status);
    const grouped = countBy(all);

    return {
      ...badgeDictionary.N,
      grouping: grouped,
    };
  },
  // Every parcel here is an "RL *" (mixedReturnAndDelivery above already claimed the stops that mix
  // returns with deliveries), so badgeForReturnStatus is a total mapping. It replaces the previous
  // `badgeDictionary[status.split(' ')[1]]`, which returned UNDEFINED for `RL IG`, `RL oGGS` (the
  // dictionary key is `oGS`) and `RL Storno` — and buildFilterBadges then reads `.value` off that
  // undefined, taking down the whole Zustellart panel rather than one badge.
  getFilterValue: (finishedDelivery: FinishedDelivery) =>
    finishedDelivery.deliveryItems?.map((dItem) => badgeForReturnStatus(dItem?.status)),
};

const differentReturnStopsWithTan: Rule = {
  shouldApply: (finishedDelivery: FinishedDelivery) => {
    if (!isNewApi(finishedDelivery)) return false;
    const { deliveryItems } = finishedDelivery;
    return deliveryItems.some((d) => d.status.startsWith('RL T'));
  },
  getValue: (finishedDelivery: FinishedDelivery) => {
    const { deliveryItems } = finishedDelivery;
    const uniqDeliveriesStatus = uniq(deliveryItems.map((d) => d.status));
    if (uniqDeliveriesStatus.length === 1 && uniqDeliveriesStatus[0].startsWith('RL T')) {
      const orderOfN = uniqDeliveriesStatus[0].split(' ')[1];
      return badgeDictionary[orderOfN];
    }

    const all = [...finishedDelivery.returnDeliveryItems, ...finishedDelivery.deliveryItems].map((s) => s.status);
    const grouped = countBy(all);

    return {
      ...badgeDictionary.N,
      grouping: grouped,
    };
  },
  // Every parcel here is an "RL *" (mixedReturnAndDelivery above already claimed the stops that mix
  // returns with deliveries), so badgeForReturnStatus is a total mapping. It replaces the previous
  // `badgeDictionary[status.split(' ')[1]]`, which returned UNDEFINED for `RL IG`, `RL oGGS` (the
  // dictionary key is `oGS`) and `RL Storno` — and buildFilterBadges then reads `.value` off that
  // undefined, taking down the whole Zustellart panel rather than one badge.
  getFilterValue: (finishedDelivery: FinishedDelivery) =>
    finishedDelivery.deliveryItems?.map((dItem) => badgeForReturnStatus(dItem?.status)),
};

const ruleSet: Rule[] = [
  noFinishedDelivery,
  paketShop,
  mixedReturnAndDelivery,
  differentReturnStopsWithN,
  differentReturnStops,
  differentReturnStopsWithTan,
  personDeliveryReturn,
  personDeliveryPlus,
  personDeliveryRule,
  undelivered,
  props,
  onTimeAuth,
  putDownOutside,
  // has to be at the end
  fallbackRule,
];

type PutDownLocationMappingType = {
  [k in PutDownLocation]: DeliveryBadge;
};

const putDownLocationMappingType: PutDownLocationMappingType = {
  [PutDownLocation.Mailbox]: badgeDictionary.BK,
  [PutDownLocation.Bag]: badgeDictionary.TU,
  [PutDownLocation.PermanentAuth]: badgeDictionary.DV,
  [PutDownLocation.OneTimeAuth]: badgeDictionary.EV,
  [PutDownLocation.BnkAuthorization]: badgeDictionary.BV,
  [PutDownLocation.UnknownAuthorization]: badgeDictionary.VM,
};

const buildDeliveryBadge = (finishedDelivery: FinishedDelivery, customer?: CustomerTrackingData): DeliveryBadge => {
  const packagesAmount = finishedDelivery
    ? finishedDelivery.deliveryItems.length + finishedDelivery.returnDeliveryItems.length
    : undefined;

  for (const r of ruleSet) {
    if (r.shouldApply(finishedDelivery, customer)) {
      return {
        ...r.getValue(finishedDelivery, customer),
        packagesAmount,
      };
    }
  }
};

export const filterStopsByBadge = (badges: Array<string>, list: FinishedDelivery[]) => {
  // if there are no filters, we can return the list unfiltered
  if (!badges.length) return list;

  return list.filter((finishedDelivery) => {
    const deliveryBadge = buildDeliveryBadge(finishedDelivery);
    if (
      deliveryBadge.value === 'N' && // an tour badge containing grouped Sendungen with status "RL N1", "RL N2", "RL N3", "RL N4"
      !!deliveryBadge['grouping'] && // has object containing grouped Sendungen with status "RL N1", "RL N2", "RL N3", "RL N4"
      badges.some((badge) => badge === 'N1' || badge === 'N2' || badge === 'N3' || badge === 'N4') // filter if entries are any of "N1", "N2", "N3", "N4"
    ) {
      // show or hide Stops containing Sendungen with "N(x)" status
      // based on tour badge grouping object
      return badges.some((badge) => deliveryBadge['grouping'][`RL ${badge}`] > 0);
    } else if (deliveryBadge.value === 'OE+' && !!deliveryBadge['grouping']) {
      return badges.some((badge) => deliveryBadge['grouping'][badge] > 0);
    } else {
      return badges.includes(deliveryBadge?.value);
    }
  });
};

export const buildFilterBadges = (stops: FinishedDelivery[]): DeliveryBadge[] => {
  return (
    stops
      // run each stop over each rule to produce filter badges list
      ?.map((stop) => {
        for (const rule of ruleSet) {
          if (rule.shouldApply(stop)) {
            if (!rule?.getFilterValue) {
              return [
                {
                  ...rule?.getValue(stop),
                  packagesAmount: stop.deliveryItems.length + stop.returnDeliveryItems.length,
                },
              ];
            }
            return rule?.getFilterValue(stop);
          }
        }
      })
      ?.flat()
      // combine similar badges together by value and count packages amount
      ?.reduce((prev: DeliveryBadge[], current: DeliveryBadge) => {
        const existingBadgeIndex = prev.findIndex((existingBadge) => existingBadge.value === current.value);
        if (existingBadgeIndex === -1) {
          prev.push({
            ...current,
            packagesAmount: current.packagesAmount || 1,
          });
        } else {
          const newPackagesAmount = (prev[existingBadgeIndex]?.packagesAmount || 0) + (current?.packagesAmount || 1);
          prev[existingBadgeIndex] = {
            ...prev[existingBadgeIndex],
            ...current,
            packagesAmount: newPackagesAmount,
          };
        }
        return prev;
      }, [])
      // set stops amount for each list entry
      .map((deliveryBadge: DeliveryBadge) => {
        return {
          ...deliveryBadge,
          stopsAmount: filterStopsByBadge([deliveryBadge?.value], stops)?.length,
        };
      })
  );
};

export default buildDeliveryBadge;
